/** Take audio maths for the Recording Studio: vocal-sound render, mono, choir-stack blend, punch-in splice. */
import { EFFECT_KEYS, buildFxChain } from "@/lib/audioEngine/fxRecipes";

/** Render any buffer through a channel's current vocal sound (same as playback, incl. Wet reverb / delay). */
export async function renderWithFx(engine, idx, buffer) {
  const c = engine.channels[idx];
  const sr = buffer.sampleRate;
  const offline = new OfflineAudioContext(2, buffer.length + Math.floor(sr * 1.5), sr);
  const fx = buildFxChain(offline, offline.destination, engine.sharedReverbIR);
  EFFECT_KEYS.forEach((k) => fx.applyEffect(k, c.fx[k], c.fxParams[k]));
  if (!engine.dryMode) { fx.nodes.reverbWet.gain.value = 1; fx.nodes.delayWet.gain.value = 1; }
  const src = offline.createBufferSource();
  src.buffer = buffer;
  src.connect(fx.input);
  src.start(0);
  return offline.startRendering();
}

const monoData = (buf) => {
  if (buf.numberOfChannels === 1) return buf.getChannelData(0);
  const a = buf.getChannelData(0);
  const b = buf.getChannelData(1);
  const out = new Float32Array(buf.length);
  for (let i = 0; i < out.length; i += 1) out[i] = (a[i] + b[i]) / 2;
  return out;
};

export function toMono(ctx, buf, fromSec = 0) {
  const from = Math.min(buf.length - 1, Math.max(0, Math.round(fromSec * buf.sampleRate)));
  const out = ctx.createBuffer(1, buf.length - from, buf.sampleRate);
  out.copyToChannel(monoData(buf).subarray(from), 0);
  return out;
}

/** Choir stack: every take is one voice, spread evenly from left to right (equal-power pan, level 1/√n). */
export function blendStack(ctx, buffers, spread = 0.75) {
  const n = buffers.length;
  const len = Math.max(...buffers.map((b) => b.length));
  const out = ctx.createBuffer(2, len, buffers[0].sampleRate);
  const L = out.getChannelData(0);
  const R = out.getChannelData(1);
  const level = 1 / Math.sqrt(n);
  buffers.forEach((b, i) => {
    const pan = n === 1 ? 0 : -spread + (2 * spread * i) / (n - 1);
    const ang = ((pan + 1) * Math.PI) / 4;
    const gl = Math.cos(ang) * level * Math.SQRT2;
    const gr = Math.sin(ang) * level * Math.SQRT2;
    const d = monoData(b);
    for (let k = 0; k < d.length; k += 1) { L[k] += d[k] * gl; R[k] += d[k] * gr; }
  });
  return out;
}

/** Punch-in: `base` with [start, end) replaced by the same span of `part` (both on the song timeline), 20 ms crossfades. */
export function spliceTake(ctx, base, part, start, end, fade = 0.02) {
  const sr = base.sampleRate;
  const len = Math.max(base.length, Math.round(end * sr));
  const out = ctx.createBuffer(1, len, sr);
  const o = out.getChannelData(0);
  const x = monoData(base);
  const y = monoData(part);
  o.set(x.subarray(0, Math.min(x.length, len)));
  const A = Math.round(start * sr);
  const B = Math.round(end * sr);
  const F = Math.max(1, Math.round(fade * sr));
  for (let i = Math.max(0, A - F); i < Math.min(len, B + F); i += 1) {
    const g = i < A ? (i - (A - F)) / F : i >= B ? 1 - (i - B) / F : 1;
    o[i] = (i < x.length ? x[i] : 0) * (1 - g) + (i < y.length ? y[i] : 0) * g;
  }
  return out;
}
